import React, { Suspense } from 'react';

import { useIntl } from 'react-intl-next';

import { AnalyticsContext } from '@atlaskit/analytics-next';
import { SmartCardProvider } from '@atlaskit/link-provider';

import { descriptionHelper } from '../../ai-answer-dialog/ai-answer-body/ai-answer-result/json/recent-work-list/description-helper/description-helper';
import { useAIAnswerContext } from '../../ai-answer-dialog/ai-answer-context';
import { type SmartLinksAnalyticsContextData } from '../../ai-answer-dialog/analytics';
import { type NLPSearchWorkResultRecentActivityDataType } from '../../ai-answer-dialog/types';
import { KnowledgeCardsRowRefresh } from '../knowledge-cards-row';
import { KnowledgeCardsRowType } from '../types';

const HoverCard = React.lazy(() =>
	import(
		/* webpackChunkName: "@atlaskit-internal_.smart-card/hover-card" */ '@atlaskit/smart-card/hover-card'
	).then(({ HoverCard }) => ({
		default: HoverCard,
	})),
);

type KnowledgeCardActivityRowProps = {
	item: NLPSearchWorkResultRecentActivityDataType;
	maxItems: number;
	onClickCallback?: () => void;
	smartLinksAnalyticsContextData: SmartLinksAnalyticsContextData;
};

export const KnowledgeCardActivityRow: React.MemoExoticComponent<({ item, onClickCallback, smartLinksAnalyticsContextData, }: KnowledgeCardActivityRowProps) => React.JSX.Element> = React.memo(
	({
		item: activity,
		onClickCallback,
		smartLinksAnalyticsContextData,
	}: KnowledgeCardActivityRowProps): React.JSX.Element => {
		const {
			userDetails: { id: searcherId },
		} = useAIAnswerContext();
		const { formatMessage, formatDate } = useIntl();

		return (
			<Suspense fallback={<></>}>
				<AnalyticsContext data={smartLinksAnalyticsContextData}>
					<SmartCardProvider>
						<HoverCard url={activity.url} key={`knowledge-card-activity-${activity.url}`}>
							<KnowledgeCardsRowRefresh
								primaryText={activity.title}
								secondaryText={`${activity.containerName} • ${descriptionHelper({
									currentUserId: searcherId || '',
									event: activity.event,
									contributors: activity.contributors,
									othersCount: activity.othersCount,
									formatMessage,
									formatDate,
									timestamp: new Date(activity.timestamp),
								})}`}
								pictureUrl={activity.iconUrl}
								type={KnowledgeCardsRowType.WORK}
								contentTypeName={activity.contentTypeName}
								onClick={() => {
									onClickCallback?.();
									window.open(activity.url, '_self', 'noopener, noreferrer');
								}}
							/>
						</HoverCard>
					</SmartCardProvider>
				</AnalyticsContext>
			</Suspense>
		);
	},
);
